import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { useFetch, tryFetchFunction } from '../../hooks/useFetch';
import { Property, getProperties } from '../../api/Property';
import { useState, useEffect } from 'react';
import LoginForm from './auth/LoginForm';
import { logIn } from '../../api/Auth';
import { setTokens } from '../../utilities/identity';
import StandardLayout from '../layouts/StandardLayout';
import SplashPage from './SplashPage';
import CenteredLayout from '../layouts/CenteredLayout';
import FormSelectLabeled from '../common/form/SelectLabeled';
import Button from '../common/form/Button';
import { browserHistory } from '../../utilities/history';
import LoadingOverlay from '../common/LoadingOverlay';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import FormValidationDisplay from '../common/form/ValidationDisplay';
import '../../styles/IntegrationForms.css';
import { redeemEntrataCode, connectEntrataProperty } from '../../api/PropertyIntegration';
import { trackAppInsightsTrace } from '../../utilities/logging';

interface Props extends RouteComponentProps<{}> {}

const EntrataConnectPage = (props: Props) => {
    const [loginSuccessful, setLoginSuccessful] = useState<boolean | undefined>(undefined);
    const [loginLoading, setLoginLoading] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string | undefined>(undefined);
    const [properties, setProperties] = useState<Property[] | undefined>(undefined);
    const [propertiesError, setPropertiesError] = useState<string | undefined>(undefined);
    const [connectLoading, setConnectLoading] = useState<boolean>(false);
    const [connectError, setConnectError] = useState<string | undefined>(undefined);

    const [selectedTour24PropertyId, setSelectedTour24PropertyId] = useState<number | undefined>(
        undefined
    );
    const [selectedEntrataPropertyId, setSelectedEntrataPropertyId] = useState<string | undefined>(
        undefined
    );

    let authCode = new URLSearchParams(props.location.search).get('auth_code');
    let cid = new URLSearchParams(props.location.search).get('cid');

    useEffect(() => {
        const timer = setTimeout(() => {
            trackAppInsightsTrace('Entrata Connect Page Visited', {
                authCode: authCode || '',
                cid: cid || ''
            });
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    const { data: redeemData, loading: redeemLoading, error: redeemError } = useFetch(
        tryFetchFunction(redeemEntrataCode, authCode),
        [],
        undefined
    );

    const loadProperties = async () => {
        let propertiesResult = await getProperties();
        if (propertiesResult.success && propertiesResult.data) {
            setProperties(propertiesResult.data);
        } else {
            setPropertiesError(propertiesResult.error || 'Error while loading tour24 properties');
        }
    };

    const handleLoginSubmit = async (username: string, password: string) => {
        setLoginLoading(true);
        let loginResult = await logIn(username, password);
        if (loginResult.success && loginResult.data) {
            setTokens(loginResult.data.jwt, loginResult.data.refreshToken);
        } else {
            setLoginError(loginResult.error || 'Login failed');
        }
        setLoginSuccessful(loginResult.success);
        setLoginLoading(false);
        // this call intentionally not awaited
        loadProperties();
        return loginResult.success;
    };

    if ((!redeemLoading && redeemError) || propertiesError) {
        return <SplashPage text={redeemError || propertiesError || 'Unknown Error'} />;
    }

    if (loginSuccessful !== true) {
        return (
            <StandardLayout>
                <LoadingOverlay show={loginLoading} inBootstrapColumn={false} />
                <LoginForm
                    onSubmit={handleLoginSubmit}
                    errorMessage={
                        !loginSuccessful && loginError
                            ? loginError
                            : loginSuccessful === undefined
                            ? undefined
                            : 'Failed to log in. Please try again.'
                    }
                />
            </StandardLayout>
        );
    }

    const saveAssociation = async () => {
        let succeeded = false;
        if (redeemData && selectedTour24PropertyId && selectedEntrataPropertyId) {
            setConnectLoading(true);
            let connectResult = await connectEntrataProperty(selectedTour24PropertyId, {
                entrataPropertyId: selectedEntrataPropertyId,
                accessToken: redeemData.accessToken,
                subdomain: redeemData.subdomain,
                userId: redeemData.userId
            });
            setConnectLoading(false);
            setConnectError(connectResult.error);
            succeeded = !connectResult.error;
        } else {
            setConnectError('Failed to connect, please choose one of each property');
        }
        setSelectedEntrataPropertyId(undefined);
        setSelectedTour24PropertyId(undefined);
        return succeeded;
    };

    const saveAndFinish = async () => {
        let saveSucceeded = await saveAssociation();
        if (saveSucceeded) {
            browserHistory.push('/admin/manage');
        }
    };

    let buttonsDisabled = !selectedEntrataPropertyId || !selectedTour24PropertyId;

    return (
        <CenteredLayout>
            <Col lg={6} md={8} sm={10} xs={12}>
                <Row>
                    <LoadingOverlay
                        inBootstrapColumn={false}
                        show={redeemLoading || connectLoading || properties === undefined}
                    />
                    <Col md={12}>
                        {connectError && <FormValidationDisplay errorMessage={connectError} />}
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <FormSelectLabeled
                            selectProps={{
                                id: 'tour24-property',
                                name: 'tour24property',
                                value: selectedTour24PropertyId
                            }}
                            selectOptions={
                                properties
                                    ? properties.map(property => ({
                                          value: property.id,
                                          label: property.name
                                      }))
                                    : []
                            }
                            onChange={(_name, value) =>
                                typeof value === 'number'
                                    ? setSelectedTour24PropertyId(value)
                                    : undefined
                            }
                            labelProps={{ labelText: 'Connect the tour24 property:' }}
                        />
                    </Col>
                    <Col md={6}>
                        <FormSelectLabeled
                            selectProps={{
                                id: 'entrata-property',
                                name: 'entrataProperty',
                                value: selectedEntrataPropertyId
                            }}
                            selectOptions={
                                (redeemData &&
                                    redeemData.properties.map(property => ({
                                        value: property.propertyId,
                                        label: property.name
                                    }))) ||
                                []
                            }
                            onChange={(_name, value) =>
                                typeof value === 'string'
                                    ? setSelectedEntrataPropertyId(value)
                                    : undefined
                            }
                            labelProps={{ labelText: 'to the Entrata property:' }}
                        />
                        <div className="entrata-connect-submit-buttons">
                            <Button
                                addlClassName="btn-secondary"
                                text="Save and Link Another"
                                onClick={saveAssociation}
                                disabled={buttonsDisabled}
                            />
                            <Button
                                text="Save and Finish"
                                onClick={saveAndFinish}
                                disabled={buttonsDisabled}
                            />
                        </div>
                    </Col>
                </Row>
            </Col>
        </CenteredLayout>
    );
};

export default EntrataConnectPage;



// WEBPACK FOOTER //
// ./src/components/pages/EntrataConnectPage.tsx